import { useEffect } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormSelectField, FormCheckboxField, FormSubmitButton, FormCancelButton, ErrorAlert, withModalMount } from '@petseo/shared';
import { FormModal } from '@/components/FormModal';
import { businessHoursSchema, type BusinessHoursFormData } from '@/schemas/businessHours.schema';
import { useCreateBusinessHours, useUpdateBusinessHours } from '../../hooks/api/hours';
import { getDayName, formatTimeDisplay } from '@/utils/datetime';
import { getTimeOptions } from '@/utils/mappers';
import type { ServiceVendorId, BusinessHoursDto, DayOfWeek } from '@/services/api/petseo.client.wrapper';
import { createServiceVendorBusinessHoursId } from '@/services/api/petseo.client.wrapper';

interface BusinessHoursFormProps {
  serviceVendorId: ServiceVendorId;
  businessHours?: BusinessHoursDto;
  dayOfWeek: DayOfWeek;
  isOpen: boolean;
  onClose: () => void;
}

const timeOptions = getTimeOptions(30, 0, 24);

function BusinessHoursFormContent({ serviceVendorId, businessHours, dayOfWeek, isOpen, onClose }: BusinessHoursFormProps) {
  const isEditing = !!businessHours;
  const createBusinessHours = useCreateBusinessHours();
  const updateBusinessHours = useUpdateBusinessHours();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<BusinessHoursFormData>({
    resolver: zodResolver(businessHoursSchema),
    defaultValues: {
      dayOfWeek: dayOfWeek,
      is24Hours: false,
      startTime: '09:00',
      endTime: '17:00',
    },
  });

  const is24Hours = useWatch({ control, name: 'is24Hours' });

  useEffect(() => {
    if (isOpen) {
      reset({
        dayOfWeek: businessHours?.dayOfWeek ?? dayOfWeek,
        is24Hours: businessHours?.is24Hours ?? false,
        startTime: formatTimeDisplay(businessHours?.startTime) || '09:00',
        endTime: formatTimeDisplay(businessHours?.endTime) || '17:00',
      });
    }
  }, [isOpen, businessHours, dayOfWeek, reset]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: BusinessHoursFormData) => {
    const payload = data.is24Hours
      ? { is24Hours: true, startTime: undefined, endTime: undefined }
      : { is24Hours: false, startTime: data.startTime ?? undefined, endTime: data.endTime ?? undefined };

    if (isEditing && businessHours?.id) {
      await updateBusinessHours.mutateAsync({
        serviceVendorId,
        businessHoursId: createServiceVendorBusinessHoursId(businessHours.id),
        data: payload,
      });
    } else {
      await createBusinessHours.mutateAsync({
        serviceVendorId,
        data: { dayOfWeek: data.dayOfWeek, ...payload },
      });
    }
    handleClose();
  };

  const isSubmitting = createBusinessHours.isPending || updateBusinessHours.isPending;
  const error = createBusinessHours.error || updateBusinessHours.error;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditing ? `Edytuj godziny: ${getDayName(dayOfWeek)}` : `Dodaj godziny: ${getDayName(dayOfWeek)}`}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="modal-body">
          {error && <ErrorAlert apiError={error} />}

          <div className="row">
            <div className="col-6">
              <FormSelectField
                control={control}
                name="startTime"
                label="Godzina rozpoczęcia"
                options={timeOptions}
                required={!is24Hours}
                disabled={is24Hours}
                error={errors.startTime}
              />
            </div>
            <div className="col-6">
              <FormSelectField
                control={control}
                name="endTime"
                label="Godzina zakończenia"
                options={timeOptions}
                required={!is24Hours}
                disabled={is24Hours}
                error={errors.endTime}
              />
            </div>
          </div>

          <FormCheckboxField
            control={control}
            name="is24Hours"
            label="Czynne całą dobę (24h)"
            description="Po zaznaczeniu placówka jest dostępna przez całą dobę w wybrany dzień"
          />
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <FormCancelButton onClick={handleClose} disabled={isSubmitting} />
          <FormSubmitButton isLoading={isSubmitting} disabled={isSubmitting} className="btn btn-primary">
            {isSubmitting ? 'Zapisywanie...' : 'Zapisz'}
          </FormSubmitButton>
        </div>
      </form>
    </FormModal>
  );
}

export const BusinessHoursForm = withModalMount(BusinessHoursFormContent);
